import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  List,
  ListItem,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import { Files, Paperclip, Trash } from '@phosphor-icons/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Accept, FileRejection, useDropzone } from 'react-dropzone';
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
  useWatch,
} from 'react-hook-form';
import { z } from 'zod';
import { UploadService } from '../../../service/UploadService';
import { useNotificationStore } from '../../../store/storeNotifications';
import { INode } from '../../NewFormBuilder/Context/FormBuilderContext';
import { usePublishedForm } from '../Context/PublishedFormContext';
import { ActionTypes } from '../Reducer';

interface InputProps {
  node: INode;
}

interface UploadedFile {
  id: string;
  file: File;
  name: string;
  size: number;
  progress: number;
  status: 'uploading' | 'complete' | 'error';
  error?: string;
  fileUrl?: string;
  response?: any;
}

export const InputSchema = z.object({
  inputText: z.string(),
  links: z.array(
    z.object({
      name: z.string(),
      url: z.string(),
    }),
  ),
});

export function Input({ node }: InputProps) {
  const setNotification = useNotificationStore(state => state.setNotification);
  const { dispatch, setIsUploading, publishedNodes } = usePublishedForm();

  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [filesPopoverAnchor, setFilesPopoverAnchor] =
    useState<HTMLButtonElement | null>(null);

  const [uploadStatus, setUploadStatus] = useState<{
    isUploading: boolean;
    fileName: string;
    progress: number;
  }>({
    isUploading: false,
    fileName: '',
    progress: 0,
  });

  const inputId = node.data.varDataInputId;

  const formsMethods = useForm({
    defaultValues: {
      inputText: '',
      links: [],
    },
    resolver: zodResolver(InputSchema),
  });

  const { fields, append, remove } = useFieldArray({
    control: formsMethods.control,
    name: 'links',
  });

  const watchedValues = useWatch({
    control: formsMethods.control,
  });

  const updateInputText = useCallback(
    (text: string) => {
      if (inputId) {
        dispatch({
          type: ActionTypes.UPDATE_INPUT_TEXT,
          payload: { inputId, inputText: text },
        });
      }
    },
    [inputId, dispatch],
  );

  const updateInputLinks = useCallback(
    (links: { name: string; url: string }[]) => {
      if (inputId) {
        dispatch({
          type: ActionTypes.UPDATE_INPUT_LINKS,
          payload: { inputId, links },
        });
      }
    },
    [inputId, dispatch],
  );

  const validLinks = useMemo(() => {
    return (watchedValues.links || [])
      .filter(link => link.name && link.url)
      .map(link => ({ name: link.name!, url: link.url! }));
  }, [watchedValues.links]);

  useEffect(() => {
    if (publishedNodes && publishedNodes.length > 0) {
      formsMethods.reset({
        inputText: '',
        links: [],
      });
      setUploadedFiles([]);
    }
  }, [publishedNodes, formsMethods]);

  useEffect(() => {
    if (!inputId) return;

    const timeoutId = setTimeout(() => {
      updateInputText(watchedValues.inputText || '');
      updateInputLinks(validLinks);
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [
    watchedValues.inputText,
    validLinks,
    inputId,
    updateInputText,
    updateInputLinks,
  ]);

  const acceptedFileTypes = useMemo(() => {
    const allowedFiles = node.data.allowedFiles || [];
    const acceptObject = {} as Accept;

    if (allowedFiles.includes('PDF')) {
      acceptObject['application/pdf'] = ['.pdf'];
    }

    if (allowedFiles.includes('TXT')) {
      acceptObject['text/plain'] = ['.txt'];
    }

    if (allowedFiles.includes('DOCX')) {
      acceptObject[
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ] = ['.docx'];
    }

    if (allowedFiles.includes('JPG') || allowedFiles.includes('JPEG')) {
      acceptObject['image/jpeg'] = ['.jpg', '.jpeg'];
    }

    if (allowedFiles.includes('PNG')) {
      acceptObject['image/png'] = ['.png'];
    }

    if (allowedFiles.includes('PPTX')) {
      acceptObject[
        'application/vnd.openxmlformats-officedocument.presentationml.presentation'
      ] = ['.pptx'];
    }

    if(allowedFiles.includes('XLSX')) {
      acceptObject['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'] = ['.xlsx'];
    }

    if(allowedFiles.includes('CSV')) {
      acceptObject['text/csv'] = ['.csv'];
    }

    if(allowedFiles.includes('XLS')) {
      acceptObject['application/vnd.ms-excel'] = ['.xls'];
    }

    return acceptObject;
  }, [node.data.allowedFiles]);

  const onDrop = async (
    acceptedFiles: File[],
    rejectedFiles: FileRejection[],
  ) => {
    if (rejectedFiles.length > 0) {
      const rejectedTypes = rejectedFiles
        .map(file => file.file.type)
        .join(', ');
      setNotification({
        message: `Rejected files: ${rejectedTypes}. Accepted types: ${Object.keys(
          acceptedFileTypes,
        ).join(', ')}`,
        type: 'error',
      });
    }

    if (acceptedFiles.length === 0) return;

    const newFiles = acceptedFiles.map(file => ({
      id: `${file.name}-${Date.now()}`,
      file,
      name: file.name,
      size: file.size,
      progress: 0,
      status: 'uploading' as const,
    }));

    setUploadedFiles(prev => [...prev, ...newFiles]);

    if (newFiles.length > 0) {
      setUploadStatus({
        isUploading: true,
        fileName: newFiles[0].name,
        progress: 0,
      });
      setIsUploading(true);
    }

    for (const fileData of newFiles) {
      await uploadFile(fileData);
    }

    setUploadStatus({
      isUploading: false,
      fileName: '',
      progress: 0,
    });
    setIsUploading(false);
  };

  const uploadFile = async (fileData: UploadedFile) => {
    try {
      const inputs = new FormData();
      inputs.append('file', fileData.file);

      const progressInterval = setInterval(() => {
        setUploadedFiles(prev =>
          prev.map(f => {
            if (f.id === fileData.id && f.progress < 90) {
              const newProgress = f.progress + 10;
              setUploadStatus(prevStatus => ({
                ...prevStatus,
                progress: newProgress,
              }));
              return { ...f, progress: newProgress };
            }
            return f;
          }),
        );
      }, 500);

      const response = await UploadService.uploadFile(inputs);

      clearInterval(progressInterval);

      updateFileWithResponse(fileData.id, response);

      setNotification({
        message: 'File uploaded successfully',
        type: 'success',
      });
    } catch (error) {
      setNotification({
        message: 'Error uploading file',
        type: 'error',
      });
    }
  };

  const updateFileWithResponse = (id: string, response: any) => {
    const fileUrl = response?.data?.src || '';
    const fileName = response?.data?.name || '';

    setUploadedFiles(prev => {
      const fileExists = prev.find(file => file.id === id);

      if (!fileExists) return prev;

      const updatedFile: UploadedFile = {
        ...fileExists,
        status: 'complete' as const,
        progress: 100,
        name: fileName || fileExists.name,
        size: fileExists.size,
        fileUrl,
        response,
      };

      if (fileUrl && fileName) {
        append({
          name: fileName,
          url: fileUrl,
        });
      }

      return prev.map(file => (file.id === id ? updatedFile : file));
    });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: acceptedFileTypes,
    onDrop,
  });

  const handleFileSelect = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;

    const acceptString = Object.entries(acceptedFileTypes)
      .map(([mimeType, extensions]) =>
        extensions.map(ext => `.${ext.replace('.', '')}`).join(','),
      )
      .join(',');

    input.accept = acceptString;

    input.onchange = event => {
      const files = (event.target as HTMLInputElement).files;
      if (files && files.length > 0) {
        const fileArray = Array.from(files);
        onDrop(fileArray, []);
      }
    };

    input.click();
  };

  const handleDeleteFile = (fileIndex: number, fileUrl: string) => {
    remove(fileIndex);

    setUploadedFiles(prev => prev.filter(file => file.fileUrl !== fileUrl));
  };

  const handleOpenFilesPopover = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    setFilesPopoverAnchor(event.currentTarget);
  };

  const handleCloseFilesPopover = () => {
    setFilesPopoverAnchor(null);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const styles = node.data.styles;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
      }}
    >
      {node.data.requiredField && (
        <Chip
          label="Required"
          size="small"
          style={{ marginBottom: '4px', width: 'fit-content' }}
        />
      )}

      <Typography
        style={{
          fontFamily: styles?.title?.fontFamily || 'Roboto, sans-serif',
          fontWeight: styles?.title?.isBold ? '700' : '400',
          fontStyle: styles?.title?.isItalic ? 'italic' : 'normal',
          textDecoration: styles?.title?.isUnderlined ? 'underline' : 'none',
          textAlign: styles?.title?.textAlign || 'center',
          fontSize: `${styles?.title?.fontSize || 16}px`,
          color: styles?.title?.color || '#1F2937',
          width: '100%',
          lineHeight: '150%',
          letterSpacing: '0.5px',
        }}
      >
        {node.data.title}
      </Typography>

      <Typography
        style={{
          color: styles.subtitle?.color || 'rgb(71, 84, 103)',
          whiteSpace: 'pre-wrap',
          fontSize: `${styles.subtitle?.fontSize || 14}px`,
          fontFamily: styles.subtitle?.fontFamily || 'Roboto, sans-serif',
          fontWeight: styles.subtitle?.isBold ? '700' : '400',
          fontStyle: styles.subtitle?.isItalic ? 'italic' : 'normal',
          textDecoration: styles.subtitle?.isUnderlined ? 'underline' : 'none',
          width: '100%',
          textAlign: styles.subtitle?.textAlign || 'left',
          lineHeight: '150%',
          letterSpacing: '0.5px',
        }}
      >
        {node.data.subtitle}
      </Typography>

      <FormProvider {...formsMethods}>
        <form
          style={{
            display: 'flex',
            flexDirection: 'column',
            marginTop: '8px',
            gap: '12px',
            height: '100%',
          }}
        >
          <Box
            {...(node.data.fieldType === 'both' ||
            node.data.fieldType === 'upload'
              ? getRootProps({ className: 'dropzone' })
              : {})}
            sx={{
              width: '100%',
              border: '1px solid #E4E7EC',
              borderRadius: '8px',
              padding: '16px',
              backgroundColor: '#FFFFFF',
              boxSizing: 'border-box',
              transition: 'all 0.2s ease-in-out',
              position: 'relative',
              cursor:
                node.data.fieldType === 'upload'
                  ? 'default'
                  : node.data.fieldType === 'data'
                  ? 'text'
                  : 'text',
              '&:hover': {
                borderColor: '#D1D5DB',
              },
              ...(isDragActive &&
                (node.data.fieldType === 'both' ||
                  node.data.fieldType === 'upload') && {
                  borderColor: '#3B82F6',
                  borderStyle: 'dashed',
                }),
            }}
          >
            {(node.data.fieldType === 'both' ||
              node.data.fieldType === 'upload') && (
              <input {...getInputProps()} style={{ display: 'none' }} />
            )}

            {isDragActive &&
              (node.data.fieldType === 'both' ||
                node.data.fieldType === 'upload') && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(59, 130, 246, 0.05)',
                    borderRadius: '8px',
                    zIndex: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontWeight: 500,
                      color: '#3B82F6',
                      textAlign: 'center',
                    }}
                  >
                    Drop files here to upload
                  </Typography>
                </Box>
              )}

            <Controller
              name="inputText"
              control={formsMethods.control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  fullWidth
                  variant="standard"
                  placeholder={
                    node.data.fieldType === 'upload'
                      ? 'Drag files here or click "Attach file"'
                      : node.data.placeholder || 'Type your answer here...'
                  }
                  multiline
                  rows={node.data.minRows || 3}
                  disabled={node.data.fieldType === 'upload'}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  onFocus={e => e.stopPropagation()}
                  onClick={e => e.stopPropagation()}
                  sx={{
                    '& .MuiInputBase-root': {
                      border: 'none',
                      paddingBottom: '28px',
                      minHeight: `${(node.data.minRows || 3) * 24 + 28}px`,
                      '&:before': {
                        borderBottom: 'none',
                      },
                      '&:after': {
                        borderBottom: 'none',
                      },
                      '&:hover:not(.Mui-disabled):before': {
                        borderBottom: 'none',
                      },
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '16px',
                      lineHeight: '1.5',
                      color:
                        node.data.fieldType === 'upload'
                          ? '#9CA3AF'
                          : '#1F2937',
                      '&::placeholder': {
                        color: '#9CA3AF',
                        opacity: 1,
                      },
                    },
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: '#9CA3AF',
                    },
                  }}
                />
              )}
            />

            {(node.data.fieldType === 'both' ||
              node.data.fieldType === 'upload') && (
              <Controller
                name="links"
                control={formsMethods.control}
                render={({ field, fieldState }) => (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: '8px',
                      left: '8px',
                      right: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <Button
                      onClick={e => {
                        e.stopPropagation();
                        handleFileSelect();
                      }}
                      startIcon={<Paperclip size={16} />}
                      sx={{
                        minWidth: 'auto',
                        minHeight: '27px',
                        height: '27px',
                        padding: '4px 8px',
                        fontSize: '12px',
                        textTransform: 'none',
                        color: '#6B7280',
                        backgroundColor: 'transparent',
                        border: '1px solid #E5E7EB',
                        borderRadius: '4px',
                        '&:hover': {
                          backgroundColor: '#F9FAFB',
                          borderColor: '#D1D5DB',
                        },
                      }}
                    >
                      Attach file
                    </Button>

                    <Button
                      onClick={e => {
                        e.stopPropagation();
                        handleOpenFilesPopover(e);
                      }}
                      startIcon={<Files size={16} />}
                      sx={{
                        minWidth: 'auto',
                        minHeight: '27px',
                        height: '27px',
                        padding: '4px 8px',
                        fontSize: '12px',
                        textTransform: 'none',
                        color: '#6B7280',
                        backgroundColor: 'transparent',
                        border: '1px solid #E5E7EB',
                        borderRadius: '4px',
                        position: 'relative',
                        '&:hover': {
                          backgroundColor: '#F9FAFB',
                          borderColor: '#D1D5DB',
                        },
                      }}
                    >
                      Files ({uploadedFiles.length})
                    </Button>

                    {uploadStatus.isUploading && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          height: '27px',
                          gap: '8px',
                          padding: '0px 8px',
                          backgroundColor: '#F3F4F6',
                          border: '1px solid #E5E7EB',
                          borderRadius: '4px',
                          fontSize: '11px',
                          color: '#6B7280',
                        }}
                      >
                        <CircularProgress
                          size={16}
                          thickness={4}
                          sx={{
                            color: '#3B82F6',
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: '11px',
                            fontWeight: 500,
                            maxWidth: '150px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {uploadStatus.fileName}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
              />
            )}
          </Box>
        </form>
      </FormProvider>

      <Popover
        open={Boolean(filesPopoverAnchor)}
        anchorEl={filesPopoverAnchor}
        onClose={handleCloseFilesPopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 1.5, minWidth: 300, maxWidth: 400 }}>
          <Typography sx={{ mb: 1, fontSize: '14px', fontWeight: 500 }}>
            Uploaded Files ({uploadedFiles.length})
          </Typography>

          {uploadedFiles.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              No files uploaded yet
            </Typography>
          ) : (
            <List dense>
              {uploadedFiles.map((file, index) => (
                <ListItem
                  key={file.id}
                  sx={{
                    border: '1px solid #E4E7EC',
                    borderRadius: 1,
                    mb: 1,
                    px: 1,
                    py: 1,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      width: '100%',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        width: '100%',
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 500,
                          fontSize: '12px',
                          cursor: file.fileUrl ? 'pointer' : 'default',
                          color: file.fileUrl ? '#3B82F6' : '#6B7280',
                          maxWidth: '200px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        onClick={() =>
                          file.fileUrl && window.open(file.fileUrl, '_blank')
                        }
                      >
                        {file.name}
                      </Typography>
                      <Trash
                        size={16}
                        color="#F05450"
                        cursor="pointer"
                        onClick={() => {
                          if (file.fileUrl) {
                            const linkIndex = fields.findIndex(
                              field => field.url === file.fileUrl,
                            );
                            if (linkIndex !== -1) {
                              handleDeleteFile(linkIndex, file.fileUrl);
                            }
                          }
                        }}
                      />
                    </div>

                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary' }}
                    >
                      {formatFileSize(file.size)}
                    </Typography>
                  </div>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Popover>
    </div>
  );
}
